import { useState } from 'react';

import Callout, { CalloutType } from '@polyai/ui/components/atoms/Callout';
import { ExternalLinkIcon } from '@polyai/ui/components/atoms/Icons';
import Input from '@polyai/ui/components/atoms/Input';
import Text from '@polyai/ui/components/atoms/Text';
import { cn } from '@polyai/ui/utils';

import { OPENTABLE_FORM_COPY } from './OpenTable.uiCopy';
import type { IntegrationFormConfig } from './types';

const OPENTABLE_LOGIN_URL = 'https://guestcenter.opentable.com/login';

const OPENTABLE_INTEGRATION_URL_PREFIX =
  'https://guestcenter.opentable.com/restaurant';

const splitStringWithBr = (text: string) => {
  return text.split('\n').flatMap((line, index, arr) => [
    line,
    // biome-ignore lint/suspicious/noArrayIndexKey: need to split on newlines and preserve them in the UI
    ...(index < arr.length - 1 ? [<br key={`br-${index}`} />] : []),
  ]);
};

const getIntegrationUrl = (rid: string) =>
  `${OPENTABLE_INTEGRATION_URL_PREFIX}/${encodeURIComponent(
    rid,
  )}/integrations/my-integrations/manage/polyai`;

const OpenTableSetupGuide = () => {
  const [rid, setRid] = useState('');
  const copy = OPENTABLE_FORM_COPY.setupGuide;

  // Step numbers shift by 1 when no RID, since "navigate to your restaurant"
  // becomes an extra step before completing the flow.
  const hasValidRid = /^\d+$/.test(rid);
  const flowStep = hasValidRid ? 2 : 3;
  const returnStep = hasValidRid ? 3 : 4;
  const reviewStep = hasValidRid ? 4 : 5;

  return (
    <div className="flex flex-col gap-sm2">
      <div className="flex flex-col gap-xs2">
        <Text variant="bodySemibold">{copy.ridLabel}</Text>
        <Text variant="bodySmall" className="text-helper">
          {copy.ridHelperText(OPENTABLE_INTEGRATION_URL_PREFIX, '12345')}{' '}
        </Text>
        <Input
          value={rid}
          label={copy.ridLabel}
          help={splitStringWithBr(copy.ridInputHelp)}
          errors={
            rid.length > 0 && !hasValidRid ? [copy.ridInvalidInputError] : []
          }
          fullWidth
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setRid(e.target.value.trim())
          }
          placeholder={copy.ridInputPlaceholder}
        />
      </div>

      {/* Step 1 — conditional on whether RID is provided */}

      <Callout
        type={CalloutType.NEUTRAL}
        hideIcon
        className={cn(
          'cursor-pointer hover:bg-background-accent transition-all duration-300',
          'ease-in-out',
        )}
      >
        <a
          className="flex justify-between gap-sm2 items-center"
          href={hasValidRid ? getIntegrationUrl(rid) : OPENTABLE_LOGIN_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <div className="flex flex-col gap-xs2 flex-1">
            <Text variant="headerSmall">
              {hasValidRid ? copy.step1WithRid.title : copy.step1Login.title}
            </Text>
            <Text variant="bodySmall" className="text-helper">
              {hasValidRid
                ? copy.step1WithRid.description(rid)
                : copy.step1Login.description}
            </Text>
          </div>
          <ExternalLinkIcon color="textHelper" size="md" />
        </a>
      </Callout>

      {!hasValidRid && (
        <Callout type={CalloutType.NEUTRAL} hideIcon>
          <div className="flex flex-col gap-xs2">
            <Text variant="headerSmall">{copy.step2Navigate.title}</Text>
            <Text variant="bodySmall" className="text-helper">
              {copy.step2Navigate.description}
            </Text>
            <Text variant="bodySmall" className="text-helper">
              {copy.ridTipText}
            </Text>
          </div>
        </Callout>
      )}

      {/* Remaining steps — always shown */}
      <Callout type={CalloutType.NEUTRAL} hideIcon>
        <div className="flex flex-col gap-xs2">
          <Text variant="headerSmall">
            {copy.stepFlowCompletion(flowStep).title}
          </Text>
          <ol className="list-decimal list-inside flex flex-col gap-xs2 mt-xs2">
            {copy
              .stepFlowCompletion(flowStep)
              .steps.map(([stepName, stepInstructions]) => (
                <li key={stepName}>
                  <Text variant="bodySmall" className="inline">
                    {stepInstructions}
                  </Text>
                </li>
              ))}
          </ol>
        </div>
      </Callout>

      <Callout type={CalloutType.NEUTRAL} hideIcon>
        <div className="flex flex-col gap-xs2">
          <Text variant="headerSmall">{copy.stepReturn(returnStep).title}</Text>
          <Text variant="bodySmall" className="text-helper">
            {copy.stepReturn(returnStep).description}
          </Text>
        </div>
      </Callout>

      <Callout type={CalloutType.NEUTRAL} hideIcon>
        <div className="flex flex-col gap-xs2">
          <Text variant="headerSmall">{copy.stepReview(reviewStep).title}</Text>
          <Text variant="bodySmall" className="text-helper">
            {copy.stepReview(reviewStep).description}
          </Text>
        </div>
      </Callout>

      <Callout type={CalloutType.NEUTRAL} hideIcon>
        <div className="flex flex-col gap-xs2">
          <Text variant="bodySemibold">{copy.additionalLocations.title}</Text>
          <Text variant="bodySmall" className="text-helper">
            {copy.additionalLocations.description}
          </Text>
        </div>
      </Callout>

      <Callout type={CalloutType.WARNING} hideIcon>
        <div className="flex flex-col gap-xs2 text-warning">
          <Text variant="bodySemibold">{copy.troubleshooting.title}</Text>
          <Text variant="bodySmall">{copy.troubleshooting.description}</Text>
        </div>
      </Callout>
    </div>
  );
};

export const OpenTableFormConfig: IntegrationFormConfig = {
  steps: [
    {
      title: OPENTABLE_FORM_COPY.formConfig.stepTitle,
      description: OPENTABLE_FORM_COPY.formConfig.stepDescription,
      docs: <OpenTableSetupGuide />,
      connectButtonLabel: OPENTABLE_FORM_COPY.formConfig.connectButtonLabel,
    },
  ],
};
